// Per-user minimap view preferences (minimap-design.md §6): layer toggles,
// size preset, collapsed. Persisted in localStorage wrapped in try/catch —
// private-mode Safari and embedded webviews throw on access; the minimap must
// work with defaults when it does.

export interface MinimapLayers {
  /** Territory fill + ownership borders. */
  readonly territory: boolean;
  /** The explored-dim + fog-hidden rendering (off = show explored fully). */
  readonly fog: boolean;
  /** City + unit icon dots. */
  readonly units: boolean;
  /** Edge-river strokes (M4b). */
  readonly rivers: boolean;
  /** Terrain-level relief shading (M4b). */
  readonly relief: boolean;
}

export type MinimapSize = 's' | 'm' | 'l';

export interface MinimapPrefs {
  readonly layers: MinimapLayers;
  readonly size: MinimapSize;
  readonly collapsed: boolean;
}

export const DEFAULT_PREFS: MinimapPrefs = {
  layers: { territory: true, fog: true, units: true, rivers: true, relief: true },
  size: 'm',
  collapsed: false,
};

/** Container height (the `--minimap-h` CSS var) per preset. */
export const SIZE_REM: Record<MinimapSize, string> = {
  s: '10rem',
  m: '15rem',
  l: '20rem',
};

const KEY = 'aod.minimap.view.v1';

/** Read stored prefs, falling back per-field to the defaults on anything unknown. */
export function loadPrefs(storage: Pick<Storage, 'getItem'> | null): MinimapPrefs {
  if (!storage) return DEFAULT_PREFS;
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<MinimapPrefs>;
    return {
      layers: { ...DEFAULT_PREFS.layers, ...(parsed.layers ?? {}) },
      size:
        parsed.size && parsed.size in SIZE_REM ? (parsed.size as MinimapSize) : DEFAULT_PREFS.size,
      collapsed: parsed.collapsed ?? DEFAULT_PREFS.collapsed,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

/** Best-effort write; a throwing store keeps the in-memory prefs anyway. */
export function savePrefs(storage: Pick<Storage, 'setItem'> | null, prefs: MinimapPrefs): void {
  if (!storage) return;
  try {
    storage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable — prefs stay session-only */
  }
}
